import { DataConverterContract } from 'components/common/bryntumScheduler/types';
import { barBaseColor, statusToColor } from 'components/common/bryntumScheduler/utils/colors';
import { parseDate } from 'components/common/bryntumScheduler/utils/date';

type EventType = 'SUMMARY_BAR' | 'MILESTONE';
interface BasicRow {
    rowId: string;
    rowName: string;
    rowType: EventType;
    eventLayout: string;
    items: any[];
}

const isEmptyRow = <T extends BasicRow>(row: T): boolean => {
    return row.items.length === 0;
};

const createResources = <T extends BasicRow>(row: T) => {
    const isRowHasNoEvents = isEmptyRow(row);
    return {
        id: row.rowId,
        name: row.rowName,
        rowType: row.rowType,
        eventLayout: 'stack',
        cls: isRowHasNoEvents ? 'no-events-row' : '',
    };
};

const createMilestoneEvent = ({ target, row }) => {
    for (const item of row.items) {
        const status = item.itemStatus ?? 'To Do';
        const date = parseDate(item.currentStartDate, `${row.rowId}.${item.itemId}.currentStartDate`);

        // not supported yet
        // const forecastFinishDate = parseDate(e.forecastFinishDate, `${row.id}.${item.itemId}.forecastFinishDate`);
        // // Normalize forecast to always be a span (point span for milestones)
        // const forecastStartDate = forecastFinishDate;

        const uniqueId = `${row.rowId}_${item.itemId}`;

        const event = {
            id: uniqueId,
            originalId: item.itemId,
            resourceId: row.rowId,
            name: item.itemName,
            isMilestone: true,
            startDate: date,
            duration: 0,
            cls: 'current-milestone',
            eventColor: statusToColor(status),
            baselineStartDate: parseDate(item.baselineStartDate, `${row.rowId}.${item.itemId}.baselineDate`),
            status,
            spi: item.spiData?.spi,
            percentDone: item.spiData?.actualPercent,
            plannedPercentDone: item.spiData?.plannedPercent,

            // // Forecast span (point for milestones) -> forecast is not supported yet
            // forecastStartDate,
            // forecastFinishDate,
        };

        target.push(event);
    }
};

const createSummaryBarEvent = ({ target, row }) => {
    const summaryItem = row.items[0];
    const status = summaryItem.itemStatus ?? 'To Do';
    const startDate = parseDate(summaryItem.currentStartDate, `${row.rowId}.summary.currentStartDate`);
    const endDate = parseDate(summaryItem.currentFinishDate, `${row.rowId}.summary.currentFinishDate`);

    // forecast is not supported yet
    // const forecastStartDate = parseDate(summaryItem.forecastStartDate, `${row.id}.summary.forecastStartDate`);
    // const forecastFinishDate = parseDate(
    //     row.summary.forecastFinishDate,
    //     `${row.id}.summary.forecastFinishDate`,
    // );

    const event = {
        id: `${row.rowId}:summary`,
        resourceId: row.rowId,
        name: summaryItem.itemName,
        isMilestone: false,
        startDate,
        endDate,
        displayEndDate: endDate,
        cls: 'poap-summary',
        eventColor: barBaseColor(status),
        baselineStartDate: parseDate(summaryItem.baselineStartDate, `${row.rowId}.summary.baselineStartDate`),
        baselineEndDate: parseDate(summaryItem.baselineFinishDate, `${row.rowId}.summary.baselineFinishDate`),
        status,
        spi: summaryItem.spiData?.spi,
        percentDone: summaryItem.spiData?.actualPercent,
        plannedPercentDone: summaryItem.spiData?.plannedPercent,
        // Forecast span (provided by BE)
        // forecastStartDate,
        // forecastFinishDate,
    };

    target.push(event);
};

const createEvent = ({ target, row }) => {
    if (row.rowType === 'SUMMARY_BAR') {
        createSummaryBarEvent({ target, row });
    } else if (row.rowType === 'MILESTONE') {
        createMilestoneEvent({ target, row });
    }
};

const isSchedulerDataValid = (dateRange) => {
    return dateRange !== undefined && dateRange !== null;
};

export const transformResponseToBryntumData = (input): DataConverterContract => {
    const rows = input.viewRows;
    const resources = rows.map(createResources);
    const rowsWithEvents = rows.filter((row) => !isEmptyRow(row));
    const events = [];
    for (const row of rowsWithEvents) {
        createEvent({ target: events, row });
    }

    const isSchedulerDateValid = isSchedulerDataValid(input.viewDateRange);
    if (!isSchedulerDateValid) {
        return {
            resources,
            events,
            startDate: undefined,
            finishDate: undefined,
            isValidDateRange: isSchedulerDateValid,
        };
    }

    return {
        resources,
        events,
        startDate: input.viewDateRange.start,
        finishDate: input.viewDateRange.finish,
        isValidDateRange: isSchedulerDateValid,
    };
};
